import React, { useCallback, useMemo } from 'react';
import { SelectBoxWithSearch } from 'apps/sigma2-ui/src/components/ui/SelectBoxWithSearch';
import { ConfigureWorkflowBaseProps } from 'apps/sigma2-ui/src/components/workflows/types';
import { WriteBackChartHopAuthMethod } from 'libs/end-to-end/src/types/databases/workflows/promotion-readiness-score-write-back/sources/chart-hop';
import { WorkflowDataSetSelector } from 'apps/sigma2-ui/src/components/workflows/WorkflowDataSetSelector';
import { isMergeHrisIntegrationDataSet } from 'libs/end-to-end/src/is-merge-hris-integration-data-set';
import { useConnectionsConfiguration } from 'apps/sigma2-ui/src/lib/hooks/useConnectionsConfiguration';

const ChartHopConnectionSelector = ({
  organizationId,
}: {
  organizationId: string;
}) => {
  const connections = useConnectionsConfiguration(organizationId);
  const isAvailable = useMemo(() => {
    if (connections.state !== 'succeeded' || !connections.config?.isEnabled) {
      return false;
    }
    const chartHop = connections.config.connections?.chartHop;
    return (
      chartHop?.isEnabled === true &&
      chartHop?.installation?.isInstalled === true
    );
  }, [connections]);

  return (
    <div className="space-y-4">
      {connections.state === 'loading' && (
        <p className="text-gray-600">Loading connections...</p>
      )}
      {connections.state === 'succeeded' && isAvailable && (
        <p className="text-sm text-gray-600">
          Using the organization&apos;s ChartHop connection.
        </p>
      )}
      {connections.state === 'succeeded' && !isAvailable && (
        <p className="text-gray-600">
          No ChartHop connection is configured for this organization. Configure
          it in Organization Settings → Connections.
        </p>
      )}
      {connections.state === 'failed' && (
        <p className="text-red-600">
          {connections.errorMessage ?? 'Failed to load connections.'}
        </p>
      )}
    </div>
  );
};

export interface ConfigureWriteBackChartHopAuthMethodProps
  extends ConfigureWorkflowBaseProps {
  authMethod: WriteBackChartHopAuthMethod | null;

  updateAuthMethod: (authMethod: WriteBackChartHopAuthMethod | null) => void;
}

export const ConfigureWriteBackChartHopAuthMethod = ({
  authMethod,
  updateAuthMethod,
  listDataSetsPattern,
  organizationId,
}: ConfigureWriteBackChartHopAuthMethodProps) => {
  const setSelectedMethod = useCallback(
    (selected: 'none' | WriteBackChartHopAuthMethod['type']) => {
      if (selected === 'none') {
        updateAuthMethod(null);
      } else if (selected !== authMethod?.type) {
        if (selected === 'connection') {
          updateAuthMethod({ type: 'connection' });
        } else {
          updateAuthMethod({
            type: selected,
          });
        }
      }
    },
    [authMethod, updateAuthMethod],
  );

  const selectedMethod = useMemo(() => {
    return authMethod?.type ?? 'none';
  }, [authMethod]);

  return (
    <div className="space-y-6 p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-xl font-semibold">Configure ChartHop Auth Method</h2>
      <p className="text-gray-600">
        Select and configure the authentication method for ChartHop.
      </p>
      <SelectBoxWithSearch
        type="single"
        list={[
          { optionLabel: 'None', optionValue: 'none' },
          { optionLabel: 'Merge', optionValue: 'merge' },
          { optionLabel: 'Connection', optionValue: 'connection' },
        ]}
        label="Select an auth method"
        selected={selectedMethod}
        setSelected={setSelectedMethod}
      />

      {authMethod?.type === 'merge' && (
        <WorkflowDataSetSelector
          dataSetId={authMethod.dataSetId ?? null}
          title={'Configure Chart Hop Merge Data Set'}
          listDataSetsPattern={listDataSetsPattern}
          setDataSetId={(dataSetId) => {
            updateAuthMethod({
              ...authMethod,
              dataSetId: dataSetId ?? undefined,
            });
          }}
          filterFn={isMergeHrisIntegrationDataSet}
        />
      )}

      {authMethod?.type === 'connection' && (
        <ChartHopConnectionSelector organizationId={organizationId} />
      )}
    </div>
  );
};
